import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { CheckCircle, CaretLeft } from 'phosphor-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { XCircleIcon, XMarkIcon } from '@heroicons/react/20/solid';

import withReport from 'components/with-report';
import { getReportPreview } from 'api/reports';
import Spinner from 'components/spinner';
import { navigateAndStorePreviousStep } from 'utils/navigation';
import { PREVIEW_REPORT_STEP } from 'constants/report-steps';
import { checkout } from 'api/checkout';
import ReportResult from 'components/shared/report-result';
import { Report } from 'graphql-api';

function PreviewReportPage({ report }: { report: Report }): JSX.Element {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { paymentFail } = state ?? {};
  const [previewData, setPreviewData] = useState<Array<Record<string, string>>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processing, setProcessing] = useState<boolean>(false);
  const [showPaymentFailMessage, setShowPaymentFailMessage] = useState<boolean>(paymentFail);

  useEffect(() => {
    void (async () => {
      setPreviewData((await getReportPreview(report?.id)) ?? []);
      setIsLoading(false);
      if (paymentFail === true) {
        window.history.replaceState(null, '', window.location.href);
      }
    })();
    const previewData = document.getElementById('preview-data');
    const previewDataOverlay = document.getElementById('preview-data-overlay');
    if (previewData != null && previewDataOverlay != null) {
      previewDataOverlay.style.width = `${previewData.offsetWidth}px`;
    }
  }, []);

  const handleGoBackClick = (): void => {
    void navigateAndStorePreviousStep(report, PREVIEW_REPORT_STEP, navigate);
  };

  const handleGetMyReport = (): void => {
    void (async () => {
      setProcessing(true);
      const res = await checkout(report?.id);
      setProcessing(false);
      window.location.href = res.url;
    })();
  };

  return (
    <div className="max-w-6xl m-auto bg-white shadow mt-8 py-16 px-14">
      {showPaymentFailMessage && (
        <div className="rounded-md bg-red-50 p-4 mb-8">
          <div className="flex">
            <div className="flex-shrink-0">
              <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">Payment failed</p>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  type="button"
                  onClick={() => {
                    setShowPaymentFailMessage(false);
                  }}
                  className="inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50">
                  <span className="sr-only">Dismiss</span>
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <a onClick={handleGoBackClick} className="btn-secondary cursor-pointer">
        <CaretLeft size={18} className="mr-2" /> Back
      </a>
      <div className="text-center">
        <CheckCircle className="mx-auto mb-6" size={72} color="#B1BECE" />
        <div className="font-extrabold text-2xl mx-auto mb-2">Your report is ready</div>
        <div className="text-base mx-auto mb-8 w-3/4">
          Your report has been generated and is now ready for submission to the State of California.
          To download your report, please first complete payment on the following screen.
        </div>
        <button
          className="btn-primary ml-4 mb-16"
          onClick={handleGetMyReport}
          disabled={processing}>
          Get my report
          {processing && <Spinner className="ml-2" />}
        </button>
        {isLoading && (
          <div className="text-center">
            <Spinner className="text-gray-500" size={48} />
          </div>
        )}
      </div>
      <div className={cx('relative overflow-x-scroll', { invisible: isLoading })}>
        <div
          id="preview-data-overlay"
          className="absolute top-0 bottom-0 left-0 w-full bg-gradient-to-b from-transparent to-white"></div>
        <ReportResult reportResultData={previewData} />
      </div>
    </div>
  );
}

export default withReport(PreviewReportPage);
